import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/formStyles.css';
import LoaderOverlay from './LoaderOverlay';
import { showAppAlert } from '../utils/alertService';
import { municipiosRJ } from '../utils/municipios';
import { EMAIL_INVALID_MESSAGE, isValidKnownProviderEmail, normalizeEmail } from '../utils/emailValidation';

const API_URL = process.env.REACT_APP_API_BASE_URL;
const USER_IMAGE_DEFAULT = process.env.REACT_APP_USER_IMAGE_DEFAULT;

function ClientRegistrationPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const companyName = searchParams.get('name') || 'Empresa não informada';
  const logoUrl = searchParams.get('logo');
  const prefilledName = searchParams.get('clientName') || '';
  const prefilledPhone = searchParams.get('clientPhone') || '';

  const [formData, setFormData] = useState({
    nome: prefilledName,
    email: '',
    password: '',
    confirmPassword: '',
    telefone: prefilledPhone,
    dataNascimento: '',
    genero: '',
    municipio: '',
    bairro: '',
  });
  const [loading, setLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const maskCelular = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  }

  const maskDate = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .replace(/(\/\d{4})\d+?$/, '$1');
  }

  const handleDateBlur = () => {
    const { dataNascimento } = formData;
    if (!dataNascimento || dataNascimento.length !== 10) {
      return; // Não valida se estiver incompleto
    }

    const dateParts = dataNascimento.split('/');
    if (dateParts.length !== 3) {
      return; // Formato inválido
    }

    const dob = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}T00:00:00`);
    const tenYearsAgo = new Date();
    const oneHundredFiftyYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    oneHundredFiftyYearsAgo.setFullYear(oneHundredFiftyYearsAgo.getFullYear() - 150);

    if (isNaN(dob.getTime())) return; // Data inválida (ex: 31/02)

    if (dob > tenYearsAgo) {
      showAppAlert('warning', 'É necessário ter pelo menos 10 anos para se cadastrar.');
    } else if (dob < oneHundredFiftyYearsAgo) {
      showAppAlert('warning', 'A data de nascimento não pode ser superior a 150 anos.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isValidKnownProviderEmail(formData.email)) {
      showAppAlert('warning', EMAIL_INVALID_MESSAGE);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showAppAlert('warning', 'As senhas não coincidem.');
      setLoading(false);
      return;
    }

    if (!formData.dataNascimento || !formData.genero || !formData.bairro) {
      showAppAlert('warning', 'Por favor, preencha todos os campos obrigatórios.');
      setLoading(false);
      return;
    }

    const dateParts = formData.dataNascimento.split('/');
    if (dateParts.length !== 3) {
      showAppAlert('warning', 'Formato de data inválido. Use dd/mm/aaaa.');
      setLoading(false);
      return;
    }
    const dob = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}T00:00:00`);
    const tenYearsAgo = new Date();
    const oneHundredFiftyYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    oneHundredFiftyYearsAgo.setFullYear(oneHundredFiftyYearsAgo.getFullYear() - 150);

    if (dob > tenYearsAgo) {
      showAppAlert('warning', 'É necessário ter pelo menos 10 anos para se cadastrar.');
      setLoading(false);
      return;
    } else if (dob < oneHundredFiftyYearsAgo) {
      showAppAlert('warning', 'A data de nascimento não pode ser superior a 150 anos.');
      setLoading(false);
      return;
    }

    const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

    const clientData = {
      ...formData,
      email: normalizeEmail(formData.email),
      dataNascimento: formattedDate,
      company_id: parseInt(companyId, 10),
      perfil: 'cliente',
      primeiroAcesso: 0,
    };
    delete clientData.confirmPassword;

    try {
      // Endpoint público para criar clientes
      const response = await fetch(`${API_URL}/clients/public-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientData),
      });

      const result = await response.json();

      if (response.status === 201 || response.status === 200) {
        showAppAlert('success', 'Cadastro realizado com sucesso! Você será redirecionado para a tela de login.');
        setTimeout(() => {
            navigate('/login');
            setLoading(false);
        }, 3000);
      } else {
        let errorMessage = result.message || result.error || 'Erro ao realizar o cadastro. Verifique os dados e tente novamente.';
        if (result.errors) {
          const errorMessages = Object.values(result.errors).flat();
          errorMessage = errorMessages.join(' ');
        }
        showAppAlert('error', errorMessage);
        setLoading(false);
      }
    } catch (error) {
      showAppAlert('error', 'Erro de conexão. Verifique sua internet e tente novamente.');
      console.error('Erro no cadastro:', error);
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', background: '#f0f2f5', minHeight: '100vh' }}>
      <LoaderOverlay isLoading={loading} message="Finalizando cadastro..." />
      <div style={{ maxWidth: '600px', width: '100%', background: 'white', border: '1px solid #ccc', borderRadius: '8px', padding: '20px 40px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        {logoUrl && (
          <img 
            src={logoUrl} 
            alt={`Logo ${companyName}`} 
            style={{ maxHeight: '80px', display: 'block', margin: '0 auto 20px' }} 
            onError={(e) => { e.target.onerror = null; e.target.src = USER_IMAGE_DEFAULT; }}
          />
        )}
        <h2 style={{ textAlign: 'center' }}>Cadastro no programa de fidelidade</h2>
        {companyName && <h3 style={{ textAlign: 'center', color: '#333', fontWeight: 'normal' }}>{companyName}</h3>}
        
        {companyName === 'Empresa não informada' ? (
          <p style={{ color: 'red', textAlign: 'center', fontWeight: 'bold' }}>
            Link de cadastro inválido. Fale com o lojista para gerar um novo link.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="label-style"><label>Nome Completo:</label><input type="text" name="nome" value={formData.nome} onChange={handleChange} readOnly={Boolean(prefilledName)} required className="input-style" /></div>
            <div className="label-style"><label>E-mail:</label><input type="email" name="email" value={formData.email} onChange={handleChange} required className="input-style" /></div>
            <div className="label-style">
              <label>Senha:</label>
              <span style={{ position: 'relative', display: 'block' }}>
                <input type={isPasswordVisible ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} required className="input-style" />
                <span
                  onMouseDown={() => setIsPasswordVisible(true)}
                  onMouseUp={() => setIsPasswordVisible(false)}
                  onMouseLeave={() => setIsPasswordVisible(false)}
                  onTouchStart={() => setIsPasswordVisible(true)}
                  onTouchEnd={() => setIsPasswordVisible(false)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8A2BE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </span>
              </span>
            </div>
            <div className="label-style">
              <label>Confirmar Senha:</label>
              <span style={{ position: 'relative', display: 'block' }}>
                <input type={isConfirmPasswordVisible ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required className="input-style" />
                <span
                  onMouseDown={() => setIsConfirmPasswordVisible(true)}
                  onMouseUp={() => setIsConfirmPasswordVisible(false)}
                  onMouseLeave={() => setIsConfirmPasswordVisible(false)}
                  onTouchStart={() => setIsConfirmPasswordVisible(true)}
                  onTouchEnd={() => setIsConfirmPasswordVisible(false)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8A2BE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </span>
              </span>
            </div>
            <div className="label-style"><label>Celular (DDD):</label><input type="text" name="telefone" value={maskCelular(formData.telefone)} onChange={e => setFormData({ ...formData, telefone: maskCelular(e.target.value) })} readOnly={Boolean(prefilledPhone)} maxLength={15} className="input-style" /></div>
            
            <div className="label-style">
              <label>Data de Nascimento:</label>
              <input
                type="text"
                name="dataNascimento"
                value={formData.dataNascimento}
                onChange={e => setFormData({ ...formData, dataNascimento: maskDate(e.target.value) })}
                onBlur={handleDateBlur}
                className="input-style"
                placeholderText="dd/mm/aaaa"
                maxLength="10"
                required
              />
            </div>
            <div className="label-style">
              <label>Gênero:</label>
              <div className="radio-group">
                <label><input type="radio" name="genero" value="masculino" checked={formData.genero === 'masculino'} onChange={handleChange} required /> Masculino</label>
                <label><input type="radio" name="genero" value="feminino" checked={formData.genero === 'feminino'} onChange={handleChange} /> Feminino</label>
                <label><input type="radio" name="genero" value="outro" checked={formData.genero === 'outro'} onChange={handleChange} /> Outro</label>
              </div>
            </div>

            <div className="form-row">
              <div className="label-style flex-1">
                <label>Município:</label>
                <select name="municipio" value={formData.municipio} onChange={handleChange} required className="input-style">
                  <option value="">Selecione um município...</option>
                  {municipiosRJ.map((mun, index) => (
                    <option key={index} value={mun}>{mun}</option>
                  ))}
                </select>
              </div>
              <div className="label-style flex-1">
                <label>Bairro:</label>
                <input type="text" name="bairro" value={formData.bairro} onChange={handleChange} required className="input-style" />
              </div>
            </div>
            
            <div className="button-container">
              <button type="submit" className="button-style" disabled={loading}>
                {loading ? 'Finalizando...' : 'Finalizar Cadastro'}
              </button>
            </div>
          </form>
        )}

        <div style={{textAlign: 'center', marginTop: '20px'}}>
            <a href="/#/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Já tenho uma conta</a>
        </div>
      </div>
    </div>
  );
}

export default ClientRegistrationPage;
